import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef } from "react";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import {
	restoreGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import {
	ShowSuccessFetch,
	showError,
	showSuccess,
	showWarning,
} from "src/utils/toast/toastUtil";
import {
	deleteKSK1120_R01,
	getKSK1120_R01AllCount,
	getKSK1120_R01DetailList,
	getKSK1120_R01List,
	patchKSK1120_R01,
	postKSK1120_R01,
} from "./Api_KSK1120_R01";
import {
	defaultKSK1120_R01DetailDto,
	KSK1120_R01DetailDto,
	KSK1120_R01Dto,
	KSK1120_R01HeaderDto,
	KSK1120_R01_VALUE_FIELDS,
} from "./Dto_KSK1120_R01";
import { useKSK1120_R01Store } from "./Store_KSK1120_R01";
import {
	createKSK1120_R01SaveDetail,
	hasKSK1120_R01InspectionValue,
	normalizeKSK1120_R01InspectionDetail,
	validateKSK1120_R01InspectionValue,
} from "./Util_KSK1120_R01";

interface ContentKSK1120R01Props {
	gridRef: React.RefObject<AgGridReact<KSK1120_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<KSK1120_R01DetailDto> | null>;
	setMessage: (message: string) => void;
}

const getKSK1120_R01DetailEditSignature = (detailList: KSK1120_R01DetailDto[]) =>
	JSON.stringify(detailList.map((detail) => [
		detail.valueIndex,
		...KSK1120_R01_VALUE_FIELDS.map((field) => String(detail[field] ?? "")),
		String(detail.remark ?? ""),
	]));

const hasKSK1120_R01PendingChanges = () => {
	const state = useKSK1120_R01Store.getState();
	const selected = state.selectedKSK1120_R01;
	if (!selected) return false;

	const originalInfo = state.originalKSK1120_R01DetailList[0]
		?? defaultKSK1120_R01DetailDto;
	const editInfo = state.editKSK1120_R01;
	const isInfoChanged =
		editInfo.inspectorEmp !== originalInfo.inspectorEmp
		|| editInfo.approvorEmpno !== originalInfo.approvorEmpno
		|| formatDate(editInfo.inspectDt) !== formatDate(originalInfo.inspectDt);
	const isDetailChanged =
		getKSK1120_R01DetailEditSignature(state.KSK1120_R01DetailList)
		!== getKSK1120_R01DetailEditSignature(state.originalKSK1120_R01DetailList);

	return isInfoChanged || isDetailChanged;
};

export const Content_KSK1120_R01 = ({
	gridRef,
	detailGridRef,
	setMessage,
}: ContentKSK1120R01Props) => {
	const {
		KSK1120_R01Filter,
		setKSK1120_R01PastFilter,
		setKSK1120_R01List,
		selectedKSK1120_R01,
		setSelectedKSK1120_R01,
		setEditKSK1120_R01,
		KSK1120_R01DetailList,
		setKSK1120_R01DetailList,
		setOriginalKSK1120_R01DetailList,
		setAllCount,
		scrollTop,
		setScrollTop,
		detailScrollTop,
		setDetailScrollTop,
		initData,
	} = useKSK1120_R01Store();
	const editKSK1120_R01 = useKSK1120_R01Store((state) => state.editKSK1120_R01);
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const detailRequestIdRef = useRef(0);

	const clearSelectionData = useCallback(() => {
		detailRequestIdRef.current += 1;
		setSelectedKSK1120_R01(undefined);
		setEditKSK1120_R01({ ...defaultKSK1120_R01DetailDto });
		setKSK1120_R01DetailList([]);
		setOriginalKSK1120_R01DetailList([]);
	}, [
		setEditKSK1120_R01,
		setKSK1120_R01DetailList,
		setOriginalKSK1120_R01DetailList,
		setSelectedKSK1120_R01,
	]);

	const fetchDetailData = useCallback(async (
		target: KSK1120_R01HeaderDto,
		preserveDetailScroll: boolean,
	) => {
		const requestId = detailRequestIdRef.current + 1;
		detailRequestIdRef.current = requestId;
		if (!preserveDetailScroll) setDetailScrollTop(null);
		setSelectedKSK1120_R01(target);

		const detailResponse = await getKSK1120_R01DetailList(
			target.transportNo,
			target.itemNo,
		);
		if (requestId !== detailRequestIdRef.current) return;
		const details = detailResponse.data.data.map(normalizeKSK1120_R01InspectionDetail);
		const info = details.length > 0
			? {
				...defaultKSK1120_R01DetailDto,
				...details[0],
				inspectType: details[0].inspectType || "OQC",
			}
			: { ...defaultKSK1120_R01DetailDto };

		setEditKSK1120_R01(info);
		setKSK1120_R01DetailList(details);
		setOriginalKSK1120_R01DetailList(details);

		if (details.length === 0) {
			showWarning("선택한 품목그룹에 등록된 출하검사 기준이 없습니다.");
		}

		if (preserveDetailScroll) {
			requestAnimationFrame(() => {
				if (detailGridRef.current?.api) {
					restoreGridScrollState(detailGridRef.current.api, detailScrollTop);
				}
			});
		} else {
			detailGridRef.current?.api.ensureIndexVisible(0, "top");
		}
	}, [
		detailGridRef,
		detailScrollTop,
		setDetailScrollTop,
		setEditKSK1120_R01,
		setKSK1120_R01DetailList,
		setOriginalKSK1120_R01DetailList,
		setSelectedKSK1120_R01,
	]);

	const executeDataFetch = useCallback(async (
		fetchType: "search" | "refetch",
	) => {
		const [listResponse, countResponse] = await Promise.all([
			getKSK1120_R01List(
				fetchType,
				KSK1120_R01Filter,
			),
			getKSK1120_R01AllCount(KSK1120_R01Filter),
		]);
		setKSK1120_R01List(listResponse.data.data);
		setAllCount(countResponse.data.data);
		setKSK1120_R01PastFilter({ ...KSK1120_R01Filter });

		return {
			list: useKSK1120_R01Store.getState().KSK1120_R01List,
			allCount: countResponse.data.data,
		};
	}, [
		KSK1120_R01Filter,
		setAllCount,
		setKSK1120_R01List,
		setKSK1120_R01PastFilter,
	]);

	const fetchData = useCallback(async () => {
		gridRef.current?.api.stopEditing();
		detailGridRef.current?.api.stopEditing();
		if (hasKSK1120_R01PendingChanges()) {
			showWarning("변경 내용을 저장하거나 초기화한 후 다시 조회해 주세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			clearSelectionData();
			setKSK1120_R01List([]);
			setScrollTop(null);
			setDetailScrollTop(null);

			const { list, allCount } = await executeDataFetch("search");
			if (list.length === 0) {
				setMessage(`조회 0건 / 전체 ${allCount.toLocaleString()}건`);
				showWarning("조회된 출하검사 대상이 없습니다.");
				return;
			}
			await fetchDetailData(list[0], false);
			gridRef.current?.api.ensureIndexVisible(0, "top");
			ShowSuccessFetch(
				setMessage,
				`조회가 완료되었습니다. (총 ${list.length.toLocaleString()}/${allCount.toLocaleString()}건)`,
			);
		} catch {
			showError("출하검사 대상 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		clearSelectionData,
		detailGridRef,
		executeDataFetch,
		gridRef,
		fetchDetailData,
		setDetailScrollTop,
		setKSK1120_R01List,
		setLoadingSpinner,
		setMessage,
		setScrollTop,
	]);

	const fetchOne = useCallback(async (target: KSK1120_R01HeaderDto) => {
		detailGridRef.current?.api.stopEditing();
		const selected = useKSK1120_R01Store.getState().selectedKSK1120_R01;
		const isSameTarget = selected?.transportNo === target.transportNo
			&& selected.itemNo === target.itemNo;
		if (hasKSK1120_R01PendingChanges()) {
			showWarning(
				isSameTarget
					? "변경 내용을 저장하거나 초기화한 후 선택을 해제해 주세요."
					: "변경 내용을 저장하거나 초기화한 후 다른 출하검사 대상을 선택해 주세요.",
			);
			return;
		}
		if (isSameTarget) {
			clearSelectionData();
			return;
		}
		setLoadingSpinner(true);
		try {
			await fetchDetailData(target, false);
		} catch {
			clearSelectionData();
			showError("검사 상세 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [clearSelectionData, detailGridRef, fetchDetailData, setLoadingSpinner]);

	const buildSaveData = useCallback((): KSK1120_R01Dto | undefined => {
		if (!selectedKSK1120_R01) {
			showWarning("저장할 출하검사 대상을 선택해 주세요.");
			return undefined;
		}
		if (!validateString(editKSK1120_R01.inspectorEmp)) {
			showWarning("검사자를 선택해 주세요.");
			return undefined;
		}
		if (KSK1120_R01DetailList.length === 0) {
			showWarning("저장할 검사 상세가 없습니다.");
			return undefined;
		}

		const normalizedDetails = KSK1120_R01DetailList.map(
			normalizeKSK1120_R01InspectionDetail,
		);
		if (!hasKSK1120_R01InspectionValue(normalizedDetails)) {
			showWarning("검사 결과를 한 건 이상 입력해 주세요.");
			return undefined;
		}
		const invalidDetail = normalizedDetails.find((detail) =>
			!validateKSK1120_R01InspectionValue(detail),
		);
		if (invalidDetail) {
			showWarning(`${invalidDetail.inspItemNm || invalidDetail.itemCd} 검사 결과 형식을 확인해 주세요.`);
			return undefined;
		}

		const info = {
			...editKSK1120_R01,
			inspectType: "OQC",
			inspectDt: editKSK1120_R01.inspectDt
				? formatDate(editKSK1120_R01.inspectDt)
				: undefined,
		};

		return {
			header: selectedKSK1120_R01,
			detailList: normalizedDetails.map((detail) =>
				createKSK1120_R01SaveDetail(detail, info)),
		};
	}, [KSK1120_R01DetailList, editKSK1120_R01, selectedKSK1120_R01]);

	const applySaveResponse = useCallback((savedData: KSK1120_R01Dto) => {
		const savedDetails = savedData.detailList.map(
			normalizeKSK1120_R01InspectionDetail,
		);
		const savedInfo = savedDetails.length > 0
			? {
				...defaultKSK1120_R01DetailDto,
				...savedDetails[0],
				inspectType: savedDetails[0].inspectType || "OQC",
			}
			: { ...defaultKSK1120_R01DetailDto };

		detailRequestIdRef.current += 1;
		setSelectedKSK1120_R01(savedData.header);
		setEditKSK1120_R01(savedInfo);
		setKSK1120_R01DetailList(savedDetails);
		setOriginalKSK1120_R01DetailList(savedDetails);
		requestAnimationFrame(() => {
			if (detailGridRef.current?.api) {
				restoreGridScrollState(detailGridRef.current.api, detailScrollTop);
			}
		});
	}, [
		detailGridRef,
		detailScrollTop,
		setEditKSK1120_R01,
		setKSK1120_R01DetailList,
		setOriginalKSK1120_R01DetailList,
		setSelectedKSK1120_R01,
	]);

	const refetchDataAfterSave = useCallback(async (
		transportNo: string,
		itemNo: string,
	) => {
		const { list } = await executeDataFetch("refetch");
		const refreshedHeader = list.find((header) =>
			header.transportNo === transportNo && header.itemNo === itemNo,
		);

		if (refreshedHeader) {
			setSelectedKSK1120_R01(refreshedHeader);
		} else {
			clearSelectionData();
			setDetailScrollTop(null);
		}
		requestAnimationFrame(() => {
			if (gridRef.current?.api) {
				restoreGridScrollState(gridRef.current.api, scrollTop);
			}
		});
	}, [
		clearSelectionData,
		executeDataFetch,
		gridRef,
		scrollTop,
		setDetailScrollTop,
		setSelectedKSK1120_R01,
	]);

	const handleSaveClick = useCallback(async () => {
		detailGridRef.current?.api.stopEditing();
		const payload = buildSaveData();
		if (!payload) return;

		const transportNo = payload.header.transportNo;
		const itemNo = payload.header.itemNo;

		setLoadingSpinner(true);
		try {
			const saveResponse =
				validateString(payload.detailList[0]?.inspectNo)
					? await patchKSK1120_R01(payload)
					: await postKSK1120_R01(payload);
			if (!saveResponse.data.successFlag) {
				showWarning("검사 결과 저장에 실패했습니다.");
				return;
			}

			const savedData = saveResponse.data.data;
			applySaveResponse(savedData);

			const successMessage = "검사 결과가 저장되었습니다.";
			showSuccess(successMessage);
			setMessage(successMessage);
			setTimeout(() => setMessage(""), 5000);

			try {
				await refetchDataAfterSave(transportNo, itemNo);
			} catch {
				showWarning("저장은 완료되었지만 대상 목록 재조회에 실패했습니다.");
			}
		} catch {
			showError("검사 결과 저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		applySaveResponse,
		buildSaveData,
		detailGridRef,
		setLoadingSpinner,
		setMessage,
		refetchDataAfterSave,
	]);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedKSK1120_R01?.inspectNo) {
			showWarning("삭제할 출하검사 결과를 선택해 주세요.");
			return;
		}

		setLoadingSpinner(true);
		try {
			const response = await deleteKSK1120_R01([selectedKSK1120_R01]);
			if (!response.data.successFlag) {
				showWarning("출하검사 결과 삭제에 실패했습니다.");
				return;
			}
			const successMessage = "출하검사 결과가 삭제되었습니다.";
			showSuccess(successMessage);
			setMessage(successMessage);
			clearSelectionData();
			await fetchData();
		} catch {
			showError("출하검사 결과 삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		clearSelectionData,
		fetchData,
		selectedKSK1120_R01,
		setLoadingSpinner,
		setMessage,
	]);

	const initContainerData = useCallback(() => {
		gridRef.current?.api.stopEditing();
		gridRef.current?.api.deselectAll();
		detailGridRef.current?.api.stopEditing();
		detailGridRef.current?.api.deselectAll();
		initData();
		setMessage("");
	}, [detailGridRef, gridRef, initData, setMessage]);

	return {
		fetchData,
		fetchOne,
		handleDeleteClick,
		handleSaveClick,
		initContainerData,
	};
};
